import React, { useState } from 'react';
import { Select, Space, Statistic, Table, Tag, Typography } from 'antd';
import { useListingCompanies } from '../../hooks/useCompanies';
import { useRawQcPerformance } from '../../hooks/useStudioOps';

function openLabel(minutes) {
    if (minutes == null) return '';
    if (minutes < 60) return `${minutes}m`;
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

const EVENT_COLORS = {
    COMPLETED: '#12b76a',
    ASSIGNED: '#2e90fa',
    HELD: '#f79009',
    REWORK_TO_ASSIGNMENT: '#f04438',
    PARTIAL_RESHOOT: '#e31b54',
    INCORRECT_MAPPING: '#b42318',
    LOGOUT_RELEASED: '#98a2b3',
    STALE_RESET: '#667085',
    REOPENED: '#7a5af8',
    RERUN_REOPENED: '#6172f3',
};

const EVENT_LABELS = {
    COMPLETED: 'Completed',
    ASSIGNED: 'Assigned',
    HELD: 'Held',
    REWORK_TO_ASSIGNMENT: 'Rework',
    PARTIAL_RESHOOT: 'Partial reshoot',
    INCORRECT_MAPPING: 'Bad mapping',
    LOGOUT_RELEASED: 'Logout',
    STALE_RESET: 'Stale reset',
    REOPENED: 'Reopened',
    RERUN_REOPENED: 'Rerun reopened',
};

function eventLabel(type) {
    return EVENT_LABELS[type] || type;
}

function bucketLabel(iso) {
    return new Date(iso).toLocaleTimeString('en-GB', {
        timeZone: 'Asia/Kolkata',
        hour: 'numeric',
        minute: '2-digit',
        hourCycle: 'h12',
    });
}

function stackTotal(counts) {
    return Object.values(counts || {}).reduce((sum, count) => sum + count, 0);
}

function presentEventTypes(slots) {
    const present = new Set();
    for (const slot of slots) {
        for (const [type, count] of Object.entries(slot.counts || {})) {
            if (count) present.add(type);
        }
    }
    const known = Object.keys(EVENT_COLORS).filter(type => present.has(type));
    const extra = [...present].filter(type => !EVENT_COLORS[type]);
    return [...known, ...extra];
}

function ShiftHistogram({ histogram }) {
    const [hover, setHover] = useState(null);
    const slots = histogram || [];
    if (!slots.length) return null;
    const types = presentEventTypes(slots);
    const max = Math.max(1, ...slots.map(slot => stackTotal(slot.counts)));
    const width = Math.max(640, slots.length * 18);
    const height = 168;
    const pad = { left: 28, right: 8, top: 8, bottom: 28 };
    const innerWidth = width - pad.left - pad.right;
    const innerHeight = height - pad.top - pad.bottom;
    const slotWidth = innerWidth / slots.length;
    const barWidth = Math.max(3, slotWidth * 0.72);
    const labelEvery = slots.length > 16 ? 4 : 2;
    const hovered = hover != null ? slots[hover] : null;

    return (
        <div>
            <Space size={12} wrap style={{ marginBottom: 8 }}>
                {types.map(type => (
                    <span key={type} style={{ fontSize: 12 }}>
                        <span
                            style={{
                                display: 'inline-block',
                                width: 8,
                                height: 8,
                                marginRight: 4,
                                background: EVENT_COLORS[type] || '#98a2b3',
                            }}
                        />
                        {eventLabel(type)}
                    </span>
                ))}
                {!types.length ? <Typography.Text type="secondary">No events this session</Typography.Text> : null}
            </Space>
            <div style={{ position: 'relative', overflowX: 'auto' }} onMouseLeave={() => setHover(null)}>
                <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} role="img" aria-label="Shift histogram">
                    {slots.map((slot, index) => {
                        const x = pad.left + slotWidth * index + (slotWidth - barWidth) / 2;
                        let y = pad.top + innerHeight;
                        const showLabel = index % labelEvery === 0 || index === slots.length - 1;
                        return (
                            <g key={slot.start} onMouseEnter={() => setHover(index)}>
                                <rect
                                    x={pad.left + slotWidth * index}
                                    y={pad.top}
                                    width={slotWidth}
                                    height={innerHeight}
                                    fill="transparent"
                                />
                                {types.map(type => {
                                    const value = slot.counts[type] || 0;
                                    const barHeight = (value / max) * innerHeight;
                                    y -= barHeight;
                                    if (!barHeight) return null;
                                    return (
                                        <rect
                                            key={type}
                                            x={x}
                                            y={y}
                                            width={barWidth}
                                            height={barHeight}
                                            fill={EVENT_COLORS[type] || '#98a2b3'}
                                        />
                                    );
                                })}
                                {showLabel ? (
                                    <text
                                        x={pad.left + slotWidth * index + slotWidth / 2}
                                        y={height - 6}
                                        textAnchor="middle"
                                        fontSize="10"
                                        fill="#667085"
                                    >
                                        {bucketLabel(slot.start)}
                                    </text>
                                ) : null}
                            </g>
                        );
                    })}
                </svg>
                {hovered ? (
                    <div
                        style={{
                            position: 'absolute',
                            left: 8,
                            top: 8,
                            background: '#fff',
                            border: '1px solid #e4e7ec',
                            borderRadius: 4,
                            padding: '6px 8px',
                            fontSize: 12,
                        }}
                    >
                        <div>{bucketLabel(hovered.start)} IST</div>
                        {types.filter(type => hovered.counts[type]).map(type => (
                            <div key={type}>
                                {eventLabel(type)}: {hovered.counts[type]}
                            </div>
                        ))}
                        {!stackTotal(hovered.counts) ? <div>No events</div> : null}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function consistencyColor(score) {
    if (score == null) return '#98a2b3';
    if (score < 20) return '#d92d20';
    if (score < 40) return '#f79009';
    if (score < 60) return '#eaaa08';
    if (score < 80) return '#12b76a';
    return '#027a48';
}

function ConsistencyDial({ score }) {
    const size = 44;
    const stroke = 4;
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const value = score == null ? 0 : Math.max(0, Math.min(100, score));
    const color = consistencyColor(score);
    return (
        <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            role="img"
            aria-label={score == null ? 'No consistency score' : `Consistency ${score}`}
        >
            <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#eaecf0" strokeWidth={stroke} />
            <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={color}
                strokeWidth={stroke}
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={circumference - (value / 100) * circumference}
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
            <text
                x={size / 2}
                y={size / 2}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize="11"
                fontWeight="600"
                fill={color}
            >
                {score == null ? '—' : score}
            </text>
        </svg>
    );
}

function currentTask(row) {
    if (!row.current) return '—';
    const name = row.current.skuName || row.current.taskName || '—';
    if (row.status === 'On hold' && row.current.holdTill) {
        const until = new Date(row.current.holdTill).toLocaleTimeString('en-GB', {
            hour: 'numeric',
            minute: 'numeric',
            hourCycle: 'h12',
        });
        return `${name} · until ${until}`;
    }
    const open = openLabel(row.current.openMinutes);
    return open ? `${name} · ${open}` : name;
}

export default function RawQcPerformance() {
    const [companyCode, setCompanyCode] = useState('');
    const { data: companyCodeList } = useListingCompanies();
    const { data, isFetching } = useRawQcPerformance(companyCode ? { companyCode } : {});
    const payload = data?.data?.data || {};
    const summary = payload.summary || {};
    const updated = payload.generatedAt
        ? new Date(payload.generatedAt).toLocaleTimeString('en-GB', {
            hour: 'numeric',
            minute: 'numeric',
            hourCycle: 'h12',
        })
        : '';
    const todayFrom = payload.windows?.todayStart
        ? new Date(payload.windows.todayStart).toLocaleString('en-GB', {
            timeZone: 'Asia/Kolkata',
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            hour: 'numeric',
            minute: '2-digit',
            hourCycle: 'h12',
        })
        : '';

    return (
        <div style={{ padding: 16 }}>
            <Space wrap style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
                <Typography.Title level={5} style={{ margin: 0 }}>
                    Raw QC performance
                </Typography.Title>
                <Space>
                    {updated ? <Typography.Text type="secondary">Updated {updated}</Typography.Text> : null}
                    <Select
                        showSearch
                        allowClear
                        placeholder="All clients"
                        style={{ width: 180 }}
                        value={companyCode || undefined}
                        onChange={value => setCompanyCode(value || '')}
                        optionFilterProp="children"
                    >
                        {(companyCodeList || []).map(company => (
                            <Select.Option key={company._id} value={company.code}>
                                {company.code}
                            </Select.Option>
                        ))}
                    </Select>
                </Space>
            </Space>
            <Typography.Paragraph type="secondary" style={{ marginTop: -8, marginBottom: 16 }}>
                Today is the session day from 6:00 am IST until 6:00 am IST the next morning.
                {todayFrom ? ` This session started ${todayFrom} IST.` : ''} Work done after midnight and before 6:00 am still belongs to the previous day.
                Consistency uses the shift with the most completed work. A gap longer than 3 hours starts a new shift, so a brief login earlier in the day does not count as idle time. A shift shorter than 9 hours is scored as 9 hours. Expand a row for that shift.
            </Typography.Paragraph>
            <Space size="large" wrap style={{ marginBottom: 16 }}>
                <Statistic title="Working now" value={summary.working || 0} />
                <Statistic title="Completed last hour" value={summary.completedLastHour || 0} />
                <Statistic title="Completed today" value={summary.completedToday || 0} />
                <Statistic title="ATH today (min)" value={summary.avgHandleMinsToday || 0} />
                <Statistic title="Rejects today" value={summary.rejectsToday || 0} />
                <Statistic title="Pending" value={summary.pending || 0} />
                <Statistic title="In progress" value={summary.inProgress || 0} />
            </Space>
            <Table
                loading={isFetching}
                rowKey="userId"
                dataSource={payload.operators || []}
                scroll={{ x: 1620 }}
                pagination={false}
                expandable={{
                    expandedRowRender: row => <ShiftHistogram histogram={row.histogram} />,
                    rowExpandable: row => (row.histogram || []).length > 0,
                }}
                columns={[
                    {
                        title: 'User',
                        render: (_, row) => (
                            <div>
                                <div>{row.name}</div>
                                <Typography.Text type="secondary">{row.email}</Typography.Text>
                            </div>
                        ),
                    },
                    {
                        title: 'Status',
                        width: 140,
                        render: (_, row) => (
                            <Space direction="vertical" size={4}>
                                <Tag color={row.status === 'Working' ? 'green' : row.status === 'On hold' ? 'gold' : 'default'}>
                                    {row.status}
                                </Tag>
                                {row.stuck ? <Tag color="red">Stuck</Tag> : null}
                            </Space>
                        ),
                    },
                    { title: 'Current task', render: (_, row) => currentTask(row) },
                    { title: 'Last hour', dataIndex: 'completedHour', width: 100 },
                    { title: 'Today', dataIndex: 'completedToday', width: 90 },
                    {
                        title: (
                            <span title="Scored on the shift with the most completions. A gap over 3 hours starts a new shift. Shifts under 9 hours are measured against a 9-hour window.">
                                Consistency
                            </span>
                        ),
                        width: 110,
                        render: (_, row) => <ConsistencyDial score={row.consistency} />,
                    },
                    { title: 'Week', dataIndex: 'completedWeek', width: 80 },
                    { title: 'ATH today', dataIndex: 'athToday', width: 100 },
                    { title: 'ATH week', dataIndex: 'athWeek', width: 100 },
                    { title: 'Rejects today', dataIndex: 'rejectsToday', width: 120 },
                    { title: 'Rejects week', dataIndex: 'rejectsWeek', width: 120 },
                    { title: 'Holds', dataIndex: 'holds', width: 80 },
                    { title: 'Logouts', dataIndex: 'logoutReleases', width: 90 },
                    { title: 'Stale resets', dataIndex: 'staleResets', width: 110 },
                    { title: 'Bad mappings', dataIndex: 'incorrectMappings', width: 120 },
                ]}
            />
        </div>
    );
}
